export const COMPLIANCE_STATUS_LABELS = {
    current: 'Current',
    expiring: 'Expiring',
    expired: 'Expired',
    verify: 'Verify',
    no_expiry: 'No expiry',
};
export const COMPLIANCE_REVIEW_STATUS_LABELS = {
    pending_review: 'Needs review',
    verified: 'Verified',
    rejected: 'Rejected',
};
export const VEHICLE_STATUS_LABELS = {
    active: 'Active',
    standby: 'Standby',
    shop_vehicle: 'Shop truck',
    in_repair: 'In repair',
    out_of_service: 'Out of service',
    disposed: 'Disposed',
};
export const VEHICLE_STATUSES = [
    'active',
    'standby',
    'shop_vehicle',
    'in_repair',
    'out_of_service',
    'disposed',
];
const MASK = '••••';
/**
 * Restricted document numbers (driver's licenses) are shown and exported
 * only as their last 4 characters. A number of 4 characters or fewer shows
 * nothing, because "the last 4" would be the whole number.
 */
export function maskDocumentNumber(value) {
    const compact = (value ?? '').replace(/\s+/g, '');
    if (!compact)
        return '';
    if (compact.length <= 4)
        return MASK;
    return `${MASK}${compact.slice(-4)}`;
}
/** The number to show or export for a record: masked when its type is restricted. */
export function documentNumberForDisplay(value, restricted, reveal = false) {
    if (!value)
        return '';
    return restricted && !reveal ? maskDocumentNumber(value) : value;
}
/**
 * Characters that make a spreadsheet treat a cell as a formula. Tab and
 * carriage return are included because some spreadsheets strip them and then
 * evaluate what follows.
 */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];
/**
 * One RFC 4180 cell. A text cell that starts like a formula gets a leading
 * apostrophe, so an exported name such as `=HYPERLINK(...)` stays text when
 * the file is opened in Excel or Sheets. Numbers are written as numbers.
 */
export function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    let text;
    if (typeof value === 'number') {
        text = Number.isFinite(value) ? String(value) : '';
    }
    else if (typeof value === 'boolean') {
        text = value ? 'true' : 'false';
    }
    else {
        text = value;
        if (text.length > 0 && FORMULA_PREFIXES.includes(text[0]))
            text = `'${text}`;
    }
    if (/[",\r\n]/.test(text))
        return `"${text.replace(/"/g, '""')}"`;
    return text;
}
/** RFC 4180: CRLF line breaks, header row first. */
export function toCsv(header, rows) {
    return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
// ── Tech self check-out ──────────────────────────────────────────────────────
export const CHECKOUT_MAX_DAYS = 30;
function pad(n) {
    return String(n).padStart(2, '0');
}
/** YYYY-MM-DD of a local calendar date. */
export function toLocalDateString(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
/** The register's calendar: the server's compliance_today() is the America/Chicago date. */
export const COMPLIANCE_TIME_ZONE = 'America/Chicago';
/** YYYY-MM-DD of the America/Chicago calendar date at `now`, whatever the device's zone. */
export function complianceToday(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: COMPLIANCE_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
}
/** A YYYY-MM-DD date plus whole calendar days (no time zone involved). */
function addCalendarDays(day, days) {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/**
 * The earliest and latest expected return dates a tech may pick: the
 * Chicago date through 30 calendar days later, the same window the server
 * checks (compliance_today() .. + 30).
 */
export function checkoutReturnBounds(now = new Date()) {
    const min = complianceToday(now);
    return { min, max: addCalendarDays(min, CHECKOUT_MAX_DAYS) };
}
/**
 * Mirrors the server rule (today through today + 30 days) so the form can say
 * what is wrong before sending. The server re-checks.
 */
export function validateCheckoutReturnDate(value, today = new Date()) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return 'Pick an expected return date.';
    const { min, max } = checkoutReturnBounds(today);
    if (value < min)
        return 'The return date cannot be in the past.';
    if (value > max)
        return `The return date must be within ${CHECKOUT_MAX_DAYS} days.`;
    return null;
}
//# sourceMappingURL=compliance.js.map